import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Block, Button, Card, Checkbox, Flex, Icon, OutsideClick, Text, Title, Tooltip } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import { useTranslation } from 'Components/Helpers/translate';
import { CopyableAddress, getAddressSummary, hasAddressValue, isSameAddress } from 'App/helpers/addressHelpers';
import './CopyAddressTo.scss';

export const COPY_ADDRESS_TYPES = ['Mail', 'Visit', 'Invoice', 'Delivery', 'Other'] as const;
export type CopyAddressType = (typeof COPY_ADDRESS_TYPES)[number];

type Props = {
	sourceType: CopyAddressType;
	source: CopyableAddress | null;
	targets: Partial<Record<CopyAddressType, CopyableAddress | null | undefined>>;
	onCopy: (types: CopyAddressType[]) => void;
	compact?: boolean;
};

type TargetStatus = 'empty' | 'same' | 'replace';

const DROPDOWN_WIDTH = 320;
const VIEWPORT_GUTTER = 8;

const getTargetStatus = (source: CopyableAddress | null, target: CopyableAddress | null | undefined): TargetStatus => {
	if (!hasAddressValue(target)) {
		return 'empty';
	}
	return isSameAddress(source, target) ? 'same' : 'replace';
};

const CopyAddressTo = ({ sourceType, source, targets, onCopy, compact = false }: Props) => {
	const { t } = useTranslation();
	const classes = new BemClass('CopyAddressTo');
	const wrapperRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
	const [floatingStyle, setFloatingStyle] = useState<React.CSSProperties | null>(null);

	useEffect(() => {
		if (!open || !compact) {
			return;
		}
		const close = (event: Event) => {
			if (!(event.target instanceof Node && dropdownRef.current?.contains(event.target))) {
				setOpen(false);
			}
		};
		window.addEventListener('resize', close);
		window.addEventListener('scroll', close, true);
		return () => {
			window.removeEventListener('resize', close);
			window.removeEventListener('scroll', close, true);
		};
	}, [open, compact]);
	const [selected, setSelected] = useState<CopyAddressType[]>([]);

	const rows = COPY_ADDRESS_TYPES.filter(type => type !== sourceType).map(type => ({
		type,
		target: targets[type],
		status: getTargetStatus(source, targets[type])
	}));

	const typesToCopy = rows.filter(row => row.status !== 'same' && selected.includes(row.type)).map(row => row.type);

	const openDropdown = () => {
		setSelected(rows.filter(row => row.status === 'empty').map(row => row.type));
		if (compact && wrapperRef.current) {
			const rect = wrapperRef.current.getBoundingClientRect();
			const top = rect.bottom + 4;
			setFloatingStyle({
				top,
				left: Math.max(VIEWPORT_GUTTER, Math.min(rect.left, window.innerWidth - DROPDOWN_WIDTH - VIEWPORT_GUTTER)),
				width: DROPDOWN_WIDTH,
				maxHeight: window.innerHeight - top - VIEWPORT_GUTTER
			});
		}
		setOpen(true);
	};

	const toggle = (type: CopyAddressType) =>
		setSelected(current =>
			current.includes(type) ? current.filter(selectedType => selectedType !== type) : [...current, type]
		);

	const copy = () => {
		onCopy(typesToCopy);
		setOpen(false);
	};

	const getDescription = (status: TargetStatus, target: CopyableAddress | null | undefined) => {
		if (status === 'empty') {
			return <Text size="sm" color="grey-10">{t('address.copy.empty')}</Text>;
		}
		if (status === 'same') {
			return <Text size="sm" color="grey-10">{t('companyMap.legend.sameAddress')}</Text>;
		}
		return (
			<Text size="sm" color="orange" className={classes.elem('replaces').b()}>
				{t('address.copy.replacesLabel')} {getAddressSummary(target ?? {})}
			</Text>
		);
	};

	const trigger = (
		<Button
			type={compact ? 'link' : 'lined'}
			color={compact ? 'grey' : undefined}
			size="sm"
			onClick={() => (open ? setOpen(false) : openDropdown())}
			disabled={!hasAddressValue(source)}
		>
			<Icon name="copy" space={compact ? undefined : 'mrs'} />
			{compact ? null : t('address.copy.button')}
		</Button>
	);

	const dropdown = (
		<div
			className={classes.elem('dropdown').mod({ floating: compact }).b()}
			ref={dropdownRef}
			style={compact && floatingStyle ? floatingStyle : undefined}
		>
			<Card borderRadius>
				<Block space="ptm prl pbs pll">
					<Title size="sm">
						{t('address.copy.title', {
							type: t(`address.${sourceType.toLowerCase()}`).toLowerCase()
						})}
					</Title>
				</Block>
				{rows.map(({ type, target, status }) => {
					const disabled = status === 'same';
					return (
						<div
							key={type}
							className={classes
								.elem('row')
								.mod({ disabled })
								.b()}
							onClick={disabled ? undefined : () => toggle(type)}
							data-test-id={`copy-address-to-${type}`}
						>
							<Checkbox
								size="sm"
								checked={!disabled && selected.includes(type)}
								disabled={disabled}
								space="mrm"
							/>
							<div>
								<Text size="sm" bold>
									{t(`address.${type.toLowerCase()}`)}
								</Text>
								{getDescription(status, target)}
							</div>
						</div>
					);
				})}
				<Flex justifyContent="flex-end" space="ptm prl pbm pll" gap="u2">
					<Button type="link" color="grey" size="sm" onClick={() => setOpen(false)}>
						{t('default.cancel')}
					</Button>
					<Button size="sm" disabled={!typesToCopy.length} onClick={copy}>
						{t('address.copy.confirm', { count: typesToCopy.length })}
					</Button>
				</Flex>
			</Card>
		</div>
	);

	return (
		<div className={classes.mod({ open }).b()} ref={wrapperRef}>
			<OutsideClick
				targetRef={() => [wrapperRef.current, dropdownRef.current]}
				outsideClick={() => setOpen(false)}
				listen={open}
			>
				{compact ? (
					<Tooltip title={t('address.copy.button')} disabled={open}>
						{trigger}
					</Tooltip>
				) : (
					trigger
				)}
				{open ? (compact ? ReactDOM.createPortal(dropdown, document.body) : dropdown) : null}
			</OutsideClick>
		</div>
	);
};

export default CopyAddressTo;
